import { useMutation } from '@apollo/client'
import { PhoneIphone } from '@mui/icons-material'
import GroupIcon from '@mui/icons-material/Group'
import InfoIcon from '@mui/icons-material/Info'
import PaymentIcon from '@mui/icons-material/Payment'
import RoomIcon from '@mui/icons-material/Room'
import { Box, Button, Chip, IconButton, Link as MuiLink, Stack, Tooltip, Typography } from '@mui/material'
import moment from 'moment'
import React from 'react'
import { Link } from 'react-router-dom'
import { downloadExcelFromBase64 } from '../../common/utils'
import type { Reservation } from '../dailyInfo/data'
import { i18n } from '../index/index'
import { Widget } from '../OldDashboard/OldDashboardComponents'
import { exportReservations } from '../OldDashboard/oldDashboardQueries'
import css from '../OldDashboard/oldReservations.css'

type Props = {
  rows: Array<Reservation>
  date: string
  stationId: string
}

const reservationTypeLabelMap: Record<string, string> = {
  '': 'dailyInfo.reservationsTable.otherTypeLabel',
  buffet: 'dailyInfo.reservationsTable.buffetTypeLabel',
  PizzaTable: 'dailyInfo.reservationsTable.pizzaTableTypeLabel',
  BuffetWorld: 'dailyInfo.reservationsTable.buffetWorldTypeLabel',
}

const typeColorMap: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  buffet: 'success',
  PizzaTable: 'warning',
  BuffetWorld: 'info',
  '': 'default',
}

const getTypeLabel = (type: string): string =>
  reservationTypeLabelMap[type] ? i18n.t(reservationTypeLabelMap[type]) : ''

const getTypeColor = (type: string) => typeColorMap[type] ?? 'default'

const hasExtraInfo = (r: Reservation) => Boolean(r.otherInfo || r.mealOptionDescription)

const formatRelativeDate = (date: moment.Moment): string => {
  const today = moment().startOf('day')
  const target = date.clone().startOf('day')
  const diff = target.diff(today, 'days')
  if (diff === 0) return `Tänään ${date.format('D.M.YYYY')}`
  if (diff === 1) return `Huomenna ${date.format('D.M.YYYY')}`
  const formatted = date.format('dddd D.M.YYYY')
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

const lineIcon = { fontSize: 20, mr: 1, mt: '3px' }

const ExtraInfoTooltip: React.FC<{ reservation: Reservation }> = ({ reservation }) => (
  <Tooltip
    placement='top-end'
    enterTouchDelay={50}
    leaveTouchDelay={10000}
    title={
      <Box sx={{ fontSize: '1em' }}>
        {reservation.otherInfo && (
          <Typography variant='body2'>
            {`${i18n.t('dailyInfo.reservationsTable.otherInfo')}: ${reservation.otherInfo}`}
          </Typography>
        )}
        {reservation.mealOptionDescription && (
          <Typography variant='body2'>{reservation.mealOptionDescription}</Typography>
        )}
      </Box>
    }
  >
    <IconButton size='small' sx={{ p: 0.5 }}>
      <InfoIcon sx={{ fontSize: 20, color: 'success.main' }} />
    </IconButton>
  </Tooltip>
)

const CardLine: React.FC<{ icon: React.ReactNode; text?: React.ReactNode }> = ({ icon, text }) => (
  <Typography
    variant='body1'
    color='text.primary'
    sx={{ display: 'flex', alignItems: 'flex-start', overflowWrap: 'anywhere' }}
  >
    {icon}
    {text || '-'}
  </Typography>
)

const ReservationCard: React.FC<{ reservation: Reservation }> = ({ reservation }) => {
  const t = moment(new Date(reservation.startTime))
  return (
    <Box
      sx={{
        border: '1px solid #e9e9e9',
        borderRadius: 2,
        p: 2,
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant='body2' color='text.primary' fontWeight={700} sx={{ mb: 0.5 }}>
        {formatRelativeDate(t)} ({t.format('HH:mm')}
        {reservation.endTime ? `–${reservation.endTime}` : ''})
      </Typography>

      <Typography variant='h6' fontWeight={700} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
        {reservation.name}
      </Typography>

      {getTypeLabel(reservation.reservationType) && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={getTypeLabel(reservation.reservationType)}
            color={getTypeColor(reservation.reservationType)}
            size='small'
          />
        </Box>
      )}

      <Stack spacing={1} sx={{ flex: 1 }}>
        <CardLine icon={<GroupIcon sx={lineIcon} />} text={reservation.peopleCount} />
        <CardLine icon={<RoomIcon sx={lineIcon} />} text={reservation.room} />
        <CardLine
          icon={<PhoneIphone sx={lineIcon} />}
          text={
            <Box component='span' sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
              <span>{reservation.contactName} –</span>
              <MuiLink href={`tel:${reservation.contactPhone}`} underline='hover' color='success.main'>
                {reservation.contactPhone ? reservation.contactPhone : 'Ei puhelinnumeroa'}
              </MuiLink>
            </Box>
          }
        />
        <CardLine icon={<PaymentIcon sx={lineIcon} />} text={reservation.paymentMethod} />
      </Stack>

      <Stack direction='row' spacing={1} justifyContent='flex-end' alignItems='center' sx={{ mt: 2 }}>
        {hasExtraInfo(reservation) && <ExtraInfoTooltip reservation={reservation} />}
        <Button
          component={Link}
          to={`/ravintolainfo/${t.format('YYYY-MM-DD')}`}
          variant='contained'
          color='success'
          size='small'
          sx={{ fontWeight: 600 }}
        >
          {i18n.t('dashboard.widget.reservations.orderDetails')}
        </Button>
      </Stack>
    </Box>
  )
}

const Reservations: React.FC<Props> = ({ rows, stationId, date }) => {
  const [exportMutation] = useMutation(exportReservations, {
    variables: { stationId, date },
    onCompleted: (data) =>
      downloadExcelFromBase64(`VARAUKSET_${moment(date).format('DD_MM_YYYY')}.xlsx`, data.exportReservations),
  })

  const hasReservations = Array.isArray(rows) && rows.length > 0

  if (!hasReservations) {
    return (
      <Widget>
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
          <Typography variant='h6' fontWeight={700}>
            {i18n.t('dashboard.widget.reservations.title')}
          </Typography>
          <Button component={Link} to='/ravintolainfo' variant='contained' color='success'>
            {i18n.t('dailyInfo.reservationsTable.addReservation')}
          </Button>
        </Stack>

        <div className={css.emptyState}>
          <Typography className={css.emptyStateText}>{i18n.t('dailyInfo.reservationsTable.noReservations')}</Typography>
        </div>
      </Widget>
    )
  }

  return (
    <Widget>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h6' fontWeight={700}>
          {i18n.t('dashboard.widget.reservations.title')}
        </Typography>
        <Button component={Link} to='/ravintolainfo' variant='contained' color='success'>
          {i18n.t('dailyInfo.reservationsTable.addReservation')}
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          minWidth: 0,
          // 1 col phone, 2 col tablet, 3 col desktop. minmax(0,1fr) = cards can shrink, never overflow.
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {rows.map((r, i) => (
          <ReservationCard key={r.id ?? i} reservation={r} />
        ))}
      </Box>

      <Stack direction='row' justifyContent={{ xs: 'stretch', sm: 'flex-end' }} sx={{ mt: 2 }}>
        <Button
          variant='contained'
          size='small'
          color='success'
          fullWidth
          sx={{ maxWidth: { sm: 'fit-content' } }}
          onClick={() => exportMutation()}
        >
          {i18n.t('reporting.label.feelingMeterNotes.export')}
        </Button>
      </Stack>
    </Widget>
  )
}

export default Reservations
